/**
 * Type definitions for Vertriebsgebiet (Sales Region) data
 */

/**
 * Milieu data for the sales region
 * Note: API may return either snake_case or PascalCase with prefix
 */
export interface MilieuData {
  milieu_name: string;
  milieu_percent: number;
  milieu_description?: string;
  milieu_relevance?: string;
}

/**
 * Household distribution data
 */
export interface HaushaltverteilungData {
  Vertriebsgebiet_Haushaltverteilung_Name: string;
  Vertriebsgebiet_Haushaltverteilung_Prozent: number;
}

/**
 * Building type data
 */
export interface GebaudeartData {
  Vertriebsgebiet_Gebaudeart_Name: string;
  Vertriebsgebiet_Gebaudeart_Prozent: number;
}

/**
 * Children per household data
 */
export interface HaushaltKinderData {
  Vertriebsgebiet_Haushalt_Kinder_Name: string;
  Vertriebsgebiet_Haushalt_Kinder_Prozent: number;
}

/**
 * Main Vertriebsgebiet data structure from API
 * Note: API may return field names with or without umlauts
 */
export interface VertriebsgebietData {
  Vertriebsgebiet_PLZ: string | number;
  Vertriebsgebiet_Anzahl_Einwohner?: number;
  Vertriebsgebiet_Anzahl_Haushalte: number;
  // Normalized field - parser handles both variants
  Vertriebsgebiet_Durchschnittliche_Haushaltsgroesse: number;
  Vertriebsgebiet_Kaufkraft_Pro_Einwohner: number;
  Vertriebsgebiet_Meistvertetener_Haushalt_Name: string;
  Vertriebsgebiet_Meistvertetener_Haushalt_Prozent: number;
  Vertriebsgebiet_Top_1_Millieu_Name: string;
  Vertriebsgebiet_Top_1_Millieu_Prozent: number;
  Vertriebsgebiet_Top_2_Millieu_Name: string;
  Vertriebsgebiet_Top_2_Millieu_Prozent: number;
  Vertriebsgebiet_Top_3_Millieu_Name: string;
  Vertriebsgebiet_Top_3_Millieu_Prozent: number;
  // Demographic data
  Vertriebsgebiet_Einwohner_Prozent_Maenner?: number;
  Vertriebsgebiet_Einwohner_Prozent_Frauen?: number;
  Vertriebsgebiet_Einwohner_Prozent_Kinder?: number;
  // Milieus array from API
  Vertriebsgebiet_Milieus?: MilieuData[];
  // Household distribution
  Vertriebsgebiet_Haushaltverteilung?: HaushaltverteilungData[];
  // Building types
  Vertriebsgebiet_Gebaudeart?: GebaudeartData[];
  // Owner/Rental ratio
  Vertriebsgebiet_Eigentuemer_Prozent?: number;
  Vertriebsgebiet_Mieter_Prozent?: number;
  // Children per household
  Vertriebsgebiet_Haushalt_Kinder?: HaushaltKinderData[];
  // Summary text from API
  Summary?: string;
}

/**
 * Combined data structure containing both main data and milieu details
 */
export interface VertriebsgebietFullData {
  main: VertriebsgebietData;
  milieus: MilieuData[];
}

/**
 * Normalize raw API data to handle field name variants (with/without umlauts)
 */
function normalizeVertriebsgebietData(raw: Record<string, unknown>): VertriebsgebietData {
  // Handle Haushaltsgröße variants (ö vs oe)
  const haushaltsgroesse =
    (raw['Vertriebsgebiet_Durchschnittliche_Haushaltsgroesse'] as number) ??
    (raw['Vertriebsgebiet_Durchschnittliche_Haushaltsgröße'] as number) ?? 0;

  return {
    Vertriebsgebiet_PLZ: raw['Vertriebsgebiet_PLZ'] as string | number,
    Vertriebsgebiet_Anzahl_Einwohner: raw['Vertriebsgebiet_Anzahl_Einwohner'] as number | undefined,
    Vertriebsgebiet_Anzahl_Haushalte: (raw['Vertriebsgebiet_Anzahl_Haushalte'] as number) ?? 0,
    Vertriebsgebiet_Durchschnittliche_Haushaltsgroesse: haushaltsgroesse,
    Vertriebsgebiet_Kaufkraft_Pro_Einwohner: (raw['Vertriebsgebiet_Kaufkraft_Pro_Einwohner'] as number) ?? 0,
    Vertriebsgebiet_Meistvertetener_Haushalt_Name: (raw['Vertriebsgebiet_Meistvertetener_Haushalt_Name'] as string) ?? '',
    Vertriebsgebiet_Meistvertetener_Haushalt_Prozent: (raw['Vertriebsgebiet_Meistvertetener_Haushalt_Prozent'] as number) ?? 0,
    Vertriebsgebiet_Top_1_Millieu_Name: (raw['Vertriebsgebiet_Top_1_Millieu_Name'] as string) ?? '',
    Vertriebsgebiet_Top_1_Millieu_Prozent: (raw['Vertriebsgebiet_Top_1_Millieu_Prozent'] as number) ?? 0,
    Vertriebsgebiet_Top_2_Millieu_Name: (raw['Vertriebsgebiet_Top_2_Millieu_Name'] as string) ?? '',
    Vertriebsgebiet_Top_2_Millieu_Prozent: (raw['Vertriebsgebiet_Top_2_Millieu_Prozent'] as number) ?? 0,
    Vertriebsgebiet_Top_3_Millieu_Name: (raw['Vertriebsgebiet_Top_3_Millieu_Name'] as string) ?? '',
    Vertriebsgebiet_Top_3_Millieu_Prozent: (raw['Vertriebsgebiet_Top_3_Millieu_Prozent'] as number) ?? 0,
    Vertriebsgebiet_Einwohner_Prozent_Maenner: raw['Vertriebsgebiet_Einwohner_Prozent_Maenner'] as number | undefined,
    Vertriebsgebiet_Einwohner_Prozent_Frauen: raw['Vertriebsgebiet_Einwohner_Prozent_Frauen'] as number | undefined,
    Vertriebsgebiet_Einwohner_Prozent_Kinder: raw['Vertriebsgebiet_Einwohner_Prozent_Kinder'] as number | undefined,
    Vertriebsgebiet_Milieus: raw['Vertriebsgebiet_Milieus'] as MilieuData[] | undefined,
    Vertriebsgebiet_Haushaltverteilung: raw['Vertriebsgebiet_Haushaltverteilung'] as HaushaltverteilungData[] | undefined,
    Vertriebsgebiet_Gebaudeart: raw['Vertriebsgebiet_Gebaudeart'] as GebaudeartData[] | undefined,
    Vertriebsgebiet_Eigentuemer_Prozent: raw['Vertriebsgebiet_Eigentuemer_Prozent'] as number | undefined,
    Vertriebsgebiet_Mieter_Prozent: raw['Vertriebsgebiet_Mieter_Prozent'] as number | undefined,
    Vertriebsgebiet_Haushalt_Kinder: raw['Vertriebsgebiet_Haushalt_Kinder'] as HaushaltKinderData[] | undefined,
    Summary: raw['Summary'] as string | undefined,
  };
}

/**
 * Parse raw JSON string from API into structured Vertriebsgebiet data
 */
export function parseVertriebsgebietData(jsonString: string): VertriebsgebietFullData | null {
  try {
    // Handle potential double-encoded JSON or wrapped string
    let data = jsonString;

    // If the string is wrapped in quotes, try to parse it first
    if (typeof data === 'string' && (data.startsWith('"') || data.startsWith("'"))) {
      try {
        data = JSON.parse(data);
      } catch {
        // If it fails, continue with original string
      }
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    // Handle case where it's already an object (not wrapped in array)
    let dataArray = parsed;
    if (!Array.isArray(parsed)) {
      // If it's an object with the expected properties, wrap it in array
      if (parsed && typeof parsed === 'object' && 'Vertriebsgebiet_PLZ' in parsed) {
        dataArray = [parsed];
      } else {
        return null;
      }
    }

    if (dataArray.length === 0) {
      return null;
    }

    // First item contains the main data - normalize field names
    const rawMainData = dataArray[0] as Record<string, unknown>;

    // Validate that we have the expected structure
    if (!rawMainData || typeof rawMainData['Vertriebsgebiet_PLZ'] === 'undefined') {
      return null;
    }

    const mainData = normalizeVertriebsgebietData(rawMainData);

    // Collect milieus from multiple sources:
    // 1. From Vertriebsgebiet_Milieus array in main data
    // 2. From remaining items in the array (legacy format with milieu_name property)
    let milieus: MilieuData[] = [];

    // Check for Vertriebsgebiet_Milieus array in main data (new API format)
    if (mainData.Vertriebsgebiet_Milieus && Array.isArray(mainData.Vertriebsgebiet_Milieus) && mainData.Vertriebsgebiet_Milieus.length > 0) {
      milieus = mainData.Vertriebsgebiet_Milieus;
    } else {
      // Legacy format: remaining items are milieu details (if they have milieu_name property)
      milieus = dataArray
        .slice(1)
        .filter((item: Record<string, unknown>) => item && 'milieu_name' in item)
        .map((item: Record<string, unknown>) => ({
          milieu_name: item.milieu_name as string,
          milieu_percent: item.milieu_percent as number,
          milieu_description: item.milieu_description as string | undefined,
          milieu_relevance: item.milieu_relevance as string | undefined,
        }));
    }

    return {
      main: mainData,
      milieus,
    };
  } catch {
    return null;
  }
}

/**
 * Generate mock data for development
 */
export function getMockVertriebsgebietData(): VertriebsgebietFullData {
  const mockData: VertriebsgebietData = {
    Vertriebsgebiet_PLZ: 40627,
    Vertriebsgebiet_Anzahl_Einwohner: 19859,
    Vertriebsgebiet_Anzahl_Haushalte: 9886,
    Vertriebsgebiet_Durchschnittliche_Haushaltsgroesse: 1.83,
    Vertriebsgebiet_Kaufkraft_Pro_Einwohner: 31988,
    Vertriebsgebiet_Meistvertetener_Haushalt_Name: '1-Personen-Haushalt',
    Vertriebsgebiet_Meistvertetener_Haushalt_Prozent: 54,
    Vertriebsgebiet_Top_1_Millieu_Name: 'Bürgerliche Mitte',
    Vertriebsgebiet_Top_1_Millieu_Prozent: 25,
    Vertriebsgebiet_Top_2_Millieu_Name: 'Traditionelles Milieu',
    Vertriebsgebiet_Top_2_Millieu_Prozent: 10,
    Vertriebsgebiet_Top_3_Millieu_Name: 'Konservativ-Etabliertes Milieu',
    Vertriebsgebiet_Top_3_Millieu_Prozent: 10,
    Vertriebsgebiet_Einwohner_Prozent_Maenner: 48.6,
    Vertriebsgebiet_Einwohner_Prozent_Frauen: 51.4,
    Vertriebsgebiet_Einwohner_Prozent_Kinder: 16.2,
    Vertriebsgebiet_Haushaltverteilung: [
      { Vertriebsgebiet_Haushaltverteilung_Name: '1-Personen-Haushalt', Vertriebsgebiet_Haushaltverteilung_Prozent: 20 },
      { Vertriebsgebiet_Haushaltverteilung_Name: '2-Personen-Haushalt', Vertriebsgebiet_Haushaltverteilung_Prozent: 20 },
      { Vertriebsgebiet_Haushaltverteilung_Name: '3-Personen-Haushalt', Vertriebsgebiet_Haushaltverteilung_Prozent: 20 },
      { Vertriebsgebiet_Haushaltverteilung_Name: '4-Personen-Haushalt', Vertriebsgebiet_Haushaltverteilung_Prozent: 20 },
      { Vertriebsgebiet_Haushaltverteilung_Name: '5+-Personen-Haushalt', Vertriebsgebiet_Haushaltverteilung_Prozent: 20 },
    ],
    Vertriebsgebiet_Gebaudeart: [
      { Vertriebsgebiet_Gebaudeart_Name: 'Einfamilienhaus', Vertriebsgebiet_Gebaudeart_Prozent: 20 },
      { Vertriebsgebiet_Gebaudeart_Name: 'Zweifamilienhaus', Vertriebsgebiet_Gebaudeart_Prozent: 20 },
      { Vertriebsgebiet_Gebaudeart_Name: 'Mehrfamilienhaus', Vertriebsgebiet_Gebaudeart_Prozent: 20 },
    ],
    Vertriebsgebiet_Eigentuemer_Prozent: 47,
    Vertriebsgebiet_Mieter_Prozent: 53,
    Vertriebsgebiet_Haushalt_Kinder: [
      { Vertriebsgebiet_Haushalt_Kinder_Name: 'Keine Kinder', Vertriebsgebiet_Haushalt_Kinder_Prozent: 20 },
      { Vertriebsgebiet_Haushalt_Kinder_Name: '1 Kind', Vertriebsgebiet_Haushalt_Kinder_Prozent: 20 },
      { Vertriebsgebiet_Haushalt_Kinder_Name: '2 Kinder', Vertriebsgebiet_Haushalt_Kinder_Prozent: 20 },
      { Vertriebsgebiet_Haushalt_Kinder_Name: '3 Kinder', Vertriebsgebiet_Haushalt_Kinder_Prozent: 20 },
      { Vertriebsgebiet_Haushalt_Kinder_Name: '3+ Kinder', Vertriebsgebiet_Haushalt_Kinder_Prozent: 20 },
    ],
    Summary: 'Das Vertriebsgebiet 40627 umfasst 19.859 Einwohner in 9.886 Haushalten mit einer durchschnittlichen Haushaltsgröße von 1,83 Personen. Die Kaufkraft liegt bei 31.988 € pro Einwohner (Index: 114). Der häufigste Haushaltstyp ist der 1-Personen-Haushalt mit 54% Anteil.',
  };

  const mockMilieus: MilieuData[] = [
    {
      milieu_name: 'Bürgerliche Mitte',
      milieu_percent: 25,
      milieu_description: 'Gut situierte, heterogene Bevölkerungsschichten, stabil sozial, hohes Bildungsniveau.',
      milieu_relevance: 'Lorem ipsum',
    },
    {
      milieu_name: 'Traditionelles Milieu',
      milieu_percent: 10,
      milieu_description: 'Ältere, weniger mobile Menschen, die großen Wert auf Traditionen und familiäre Werte legen.',
      milieu_relevance: 'Lorem ipsum',
    },
    {
      milieu_name: 'Konservativ-Etabliertes Milieu',
      milieu_percent: 10,
      milieu_description: 'Gut situierte Menschen mit traditionellen Berufshintergründen, die stabilität und gesellschaftliche Anerkennung suchen.',
      milieu_relevance: 'Lorem ipsum',
    },
  ];

  return {
    main: mockData,
    milieus: mockMilieus,
  };
}
